import { getEmptyImageField } from '@/utils/emptyTemplate';
import { EProductImageType } from '@/typescript/enums/EProductImageType';
import { fetchProductImage } from '@/api/productImage.api';
import { CUTTING_GROUP_NAME, CUTTING_OUTLINE_NAME } from '@/littleWedding/fabricGroupsName';
import { createCuttingGroup, getFabricGroupById } from '@/commons/fabricjs/FabricGroupHelper';
import { convertFromLowDPIToHighDPI } from '@/utils/convertmmPixel';
import { FabricObject, loadSVGFromURL, Rect, util } from 'fabric';
import type { IProductImage } from '@/typescript/interfaces/IProductImage';
import type { EProductFormat } from '@/typescript/enums/EProductFormat';
import type { IProduct } from '@/typescript/interfaces/IProduct';
import type { IImageField } from '@/typescript/interfaces/IImageField';
import type { TSides } from '@/typescript/interfaces/TSide';
import type { IGroup } from '@/typescript/interfaces/IGroup';
import type { WFabricWorld } from '@/littleWedding/WFabricWorld';

async function loadCutField(productImages: IProductImage[]): Promise<File | undefined> {
    const cutPathProductImage = productImages.find((img) => img.type === EProductImageType.CUTTING_AREA_RECTO);
    if (!cutPathProductImage) {
        return undefined;
    }
    const cutPathImageAsBlob = await fetchProductImage(cutPathProductImage.uuid);
    return new File([cutPathImageAsBlob], cutPathProductImage.filename, { type: cutPathImageAsBlob.type });
}

function prepareCutPathField(file: File, productFormat: EProductFormat) {
    const newImageField = { ...getEmptyImageField(), side: 'recto' as TSides };
    newImageField.uploadType = 'cutPath';
    newImageField.url = URL.createObjectURL(file);
    newImageField.filename = file.name;
    const newImageFieldList = [newImageField];
    if (productFormat === 'RECTO_VERSO') {
        const duplicateField = { ...getEmptyImageField(), side: 'verso' as TSides };
        duplicateField.filename = file.name;
        duplicateField.uploadType = 'cutPath';
        duplicateField.id = `${newImageField.id}-verso`;
        duplicateField.url = newImageField.url;
        newImageFieldList.push(duplicateField);
    }
    return newImageFieldList;
}

async function loadAndPrepareCutField(product: IProduct): Promise<IImageField[]> {
    const productImages = product.images;
    const file = await loadCutField(productImages);
    if (!file) {
        return [];
    }
    return prepareCutPathField(file, product.format);
}

function cutPathWidth(width: number): number {
    const scaleRatio = 3000; // Adjust this ratio to change the stroke width scaling
    return width / scaleRatio;
}

/**
 * deal with multiPath object inside an svg file
 * @param svgCuttingField
 */
function setTranformSvgPosition<T extends FabricObject>(toggler: { value: boolean }, svgCuttingField: IImageField, fabricPathObject: T) {
    // At first we need to test if vale are setted or not
    // If not then toggler.value became true and we test those value for good.
    // not necessary if field values are already setted
    if (toggler.value || svgCuttingField.transform.position.x + svgCuttingField.transform.position.y === 0) {
        if (toggler.value && svgCuttingField.transform.position.y > fabricPathObject.top) {
            svgCuttingField.transform.position.y = fabricPathObject.top;
        }
        if (toggler.value && svgCuttingField.transform.position.x > fabricPathObject.left) {
            svgCuttingField.transform.position.x = fabricPathObject.left;
        }
        if (!toggler.value) {
            svgCuttingField.transform.position.y = fabricPathObject.top;
            svgCuttingField.transform.position.x = fabricPathObject.left;
        }
        toggler.value = true;
    }
}

function addCuttingPathField(svgCuttingField: IImageField, fabricWorld: WFabricWorld, isAdmin = false, isProductEdition = false) {
    if (isAdmin && isProductEdition) {
        return addCuttingPathFieldAdminMode(svgCuttingField, fabricWorld);
    } else {
        return addCuttingPathFieldUserMode(svgCuttingField, fabricWorld);
    }
}

async function addCuttingPathFieldAdminMode(svgCuttingField: IImageField, fabricWorld: WFabricWorld) {
    if (!fabricWorld.fabricCanvas) {
        return;
    }
    let cuttingGroup = getFabricGroupById(fabricWorld.fabricCanvas, CUTTING_GROUP_NAME);
    if (!cuttingGroup) {
        cuttingGroup = createCuttingGroup(fabricWorld.fabricCanvas, CUTTING_GROUP_NAME);
    }
    const svgGroup = util.groupSVGElements([], {
        absolutePositioned: true,
        inverted: true,
    }) as IGroup;
    // reset group if cutting group is already hydrate - allow replacement for cutting path
    if (cuttingGroup.getObjects().length !== 0) {
        cuttingGroup?.remove(...cuttingGroup.getObjects());
    }
    svgGroup.selectable = true;
    svgGroup.evented = true;
    svgGroup.lockMovementX = true;
    svgGroup.lockMovementY = true;
    svgGroup.lockScalingX = true;
    svgGroup.lockScalingY = true;
    svgGroup.lockRotation = true;
    cuttingGroup.selectable = true;
    cuttingGroup.evented = true;
    cuttingGroup.lockMovementX = true;
    cuttingGroup.lockMovementY = true;
    cuttingGroup.lockScalingX = true;
    cuttingGroup.lockScalingY = true;
    cuttingGroup.lockRotation = true;
    cuttingGroup.add(svgGroup);

    const svgfieldToSvgUrl = new URL(svgCuttingField?.url ?? '', import.meta.url).href;
    /**
     * @note needed for multiCuttingPathBehavior behavior
     */
    const toggler = { value: false };
    await loadSVGFromURL(svgfieldToSvgUrl, (svgObject, fabricPathObject) => {
        setTranformSvgPosition(toggler, svgCuttingField, fabricPathObject);
        fabricPathObject.set({
            fill: '',
            stroke: '#00C0FA',
            strokeWidth: cutPathWidth(fabricWorld.imageRenderWidth),
            // width: convertFromLowDPIToHighDPI(fabricPathObject.width),
            // height: convertFromLowDPIToHighDPI(fabricPathObject.height),
            scaleX: convertFromLowDPIToHighDPI(1),
            scaleY: convertFromLowDPIToHighDPI(1),
        });
        svgGroup.add(fabricPathObject);
    });
    // TODO Multi-shape
    // Idea is to remove scale for each shape and apply it to the group only
    // problem is that fabric group dont take size according to scale but to children shapes size
    // I put in pause for now to launch functionnal review by client on one cut shape.
    // Consider uncommenting next lines if multi-shape cutting path is needed in the future
    // svgGroup.set('originX', 'center');
    // svgGroup.set('originY', 'center');
    // svgGroup.set('scaleX', convertFromLowDPIToHighDPI(1));
    // svgGroup.set('scaleY', convertFromLowDPIToHighDPI(1));
    // svgGroup.set('width',  convertFromLowDPIToHighDPI(svgGroup.width));
    // svgGroup.set('height', convertFromLowDPIToHighDPI(svgGroup.height));
    // svgGroup.setCoords();
    // svgGroup.set('dirty', true);
    cuttingGroup.set('left', fabricWorld.imageRenderWidth / 2);
    cuttingGroup.set('top', fabricWorld.imageRenderHeight / 2);
    cuttingGroup.set('angle', svgCuttingField.transform.angle);
    cuttingGroup.set('scaleX', svgCuttingField.transform.scale.x);
    cuttingGroup.set('scaleY', svgCuttingField.transform.scale.y);
    cuttingGroup.set('originX', 'center');
    cuttingGroup.set('originY', 'center');
    cuttingGroup.setCoords();
    cuttingGroup.set('dirty', true);
    fabricWorld.fabricCanvas.bringObjectToFront(cuttingGroup);
    fabricWorld.fabricCanvas.renderAll();
}

async function addCuttingPathFieldUserMode(svgCuttingField: IImageField, fabricWorld: WFabricWorld) {
    if (!fabricWorld.fabricCanvas) {
        return undefined;
    }
    const { fabricCanvas } = fabricWorld;
    let cuttingGroup = getFabricGroupById(fabricCanvas, CUTTING_GROUP_NAME);
    if (!cuttingGroup) {
        cuttingGroup = createCuttingGroup(fabricCanvas, CUTTING_GROUP_NAME);
    }
    let { height, width } = fabricCanvas;
    if (cuttingGroup.getObjects().length !== 0) {
        cuttingGroup?.remove(...cuttingGroup.getObjects());
    }
    const bgGroup = getFabricGroupById(fabricCanvas);
    if (bgGroup) {
        height = bgGroup.height * bgGroup.scaleY;
        width = bgGroup.width * bgGroup.scaleX;
    }
    height = Math.floor(height);
    width = Math.floor(width);
    cuttingGroup.set('objectCaching', false);

    const rect = new Rect({
        id: svgCuttingField.id,
        evented: false,
        fill: 'white',
        strokeWidth: 0,
        height: height,
        left: 0,
        selectable: false,
        top: 0,
        width: width,
        objectCaching: false,
    });
    cuttingGroup.add(rect);

    // this group allows you to display cutting path
    const svgOutlineGroup = util.groupSVGElements([], {
        absolutePositioned: true,
        selectable: false,
        evented: false,
        left: Math.floor(width / 2),
        top: Math.floor(height / 2),
        originX: 'center',
        originY: 'center',
        objectCaching: false,
    });

    svgOutlineGroup.set('id', CUTTING_OUTLINE_NAME);

    fabricCanvas?.add(svgOutlineGroup);

    // Clip path contain svg used to clip the cutting group which is loaded after
    const clipPathGroup = util.groupSVGElements([], {
        absolutePositioned: true,
        inverted: true,
    });
    cuttingGroup.clipPath = clipPathGroup;

    const svgfieldToSvgUrl = new URL(svgCuttingField?.url ?? '', import.meta.url).href;
    /**
     * @note needed for multiCuttingPathBehavior behavior
     */
    const toggler = { value: false };
    return loadSVGFromURL(svgfieldToSvgUrl, (svgObject, fabricPathObject) => {
        fabricPathObject.objectCaching = true;
        setTranformSvgPosition(toggler, svgCuttingField, fabricPathObject);
        fabricPathObject.set({
            fill: '#000000',
            originX: 'center',
            originY: 'center',
            scaleX: convertFromLowDPIToHighDPI(1),
            scaleY: convertFromLowDPIToHighDPI(1),
            strokeWidth: 0,
        });
        (clipPathGroup as IGroup).add(fabricPathObject);

        fabricPathObject.clone().then((clonedPath: FabricObject) => {
            clonedPath.set({
                stroke: '#00C0FA',
                fill: '',
                strokeWidth: cutPathWidth(width),
            });
            (svgOutlineGroup as IGroup).add(clonedPath);
        });
    }).then(() => {
        clipPathGroup.set('left', Math.floor(width / 2));
        clipPathGroup.set('top', Math.floor(height / 2));
        clipPathGroup.set('originX', 'center');
        clipPathGroup.set('originY', 'center');
        clipPathGroup.set('dirty', true);
        clipPathGroup.setCoords();

        svgOutlineGroup.set('left', Math.floor(width / 2));
        svgOutlineGroup.set('top', Math.floor(height / 2));
        svgOutlineGroup.set('dirty', true);
        svgOutlineGroup.setCoords();

        fabricCanvas.bringObjectToFront(cuttingGroup);
        fabricWorld.keepGridImageUpFront();
        fabricCanvas.bringObjectToFront(svgOutlineGroup);

        fabricCanvas.renderAll();
    });
}

function addSymetricalPathField(svgCuttingField: IImageField, fabricWorld: WFabricWorld, isAdmin = false, isProductEdition = false) {
    addCuttingPathField(svgCuttingField, fabricWorld, isAdmin, isProductEdition).then(() => {
        if (!fabricWorld.fabricCanvas) {
            return;
        }
        const cuttingGroup = getFabricGroupById(fabricWorld.fabricCanvas, CUTTING_GROUP_NAME);
        const cuttingGroupOutline = getFabricGroupById(fabricWorld.fabricCanvas, CUTTING_OUTLINE_NAME);
        if (cuttingGroup && cuttingGroupOutline) {
            if (cuttingGroup.clipPath) {
                cuttingGroup.clipPath.set('scaleX', cuttingGroup.clipPath.scaleX * -1);
            }
            cuttingGroup.set('scaleX', cuttingGroup.scaleX * -1);
            cuttingGroup.setCoords();
            cuttingGroup.set('dirty', true);

            cuttingGroupOutline.set('scaleX', cuttingGroupOutline.scaleX * -1);
            cuttingGroupOutline.setCoords();
            cuttingGroupOutline.set('dirty', true);

            fabricWorld.fabricCanvas.renderAll();
        }
    });
}

export { addCuttingPathField, addSymetricalPathField, loadAndPrepareCutField, loadCutField, prepareCutPathField };
